// ============================================================
// LOVIN MALTA — ELECTION PERSONALITY QUIZ
// Brand tokens: CONFIRMED from lovinmalta.com (v2.0 — April 2026)
// Fonts: Poppins (display) + PT Serif (body)
// Primary: #FF1349
//
// Self-contained: imports nothing from the dashboard's tokens or styles.
// CSS is injected on mount and torn down on unmount; classes are scoped
// to .lm / .q-card / .ans / .cta / .mc / .you / .lm-steps so they will
// not collide with the surrounding dashboard styles.
//
// All data — questions, MPs, and answers — comes from /api/quiz via the
// useQuiz hook. MPs who did not answer a given question simply have no
// entry in their AnswerMap; matching, compass, and matrix all skip
// missing cells rather than treating them as a default value.
// ============================================================

import { useEffect, useMemo, useState } from 'react';
import {
  useQuiz,
  type AnswerMap,
  type AnswersByMP,
  type QuizData,
  type QuizMP,
  type QuizQuestion,
} from '../../lib/useQuiz';
import {
  trackQuizStarted,
  trackQuizCompleted,
  type QuizCompletionPayload,
} from '../../lib/trackEvent';

// ---- Persisted quiz state -----------------------------------
//
// Lifted to the QuizRunner level (was previously split across
// QuizRunner + Questionnaire's internal `answers` state) so that
// navigating back to the questionnaire via the step indicator no
// longer wipes the user's selections, and so a full state snapshot
// can round-trip through localStorage across refreshes.
//
// `startedTracked` / `completedTracked` are sticky flags that ensure
// the quiz-started and quiz-completed analytics events fire at most
// once per browser-storage lifetime — refreshing mid-quiz won't
// double-count.

const QUIZ_STORAGE_KEY = 'lm-quiz-state-v1';

interface PersistedQuizState {
  started: boolean;
  districtPicked: boolean;
  voterDistrict: number | null;
  step: number;
  /** Highest step the visitor has reached so far. The step indicator
   *  treats every step ≤ maxVisitedStep as navigable; steps beyond it
   *  stay disabled. Means once you've seen Matrix you can hop back to
   *  Compass and forward to Matrix again from the top tracker. */
  maxVisitedStep: number;
  voterAnswers: AnswerMap;
  startedTracked: boolean;
  completedTracked: boolean;
}

const QUIZ_DEFAULTS: PersistedQuizState = {
  started: false,
  districtPicked: false,
  voterDistrict: null,
  step: 0,
  maxVisitedStep: 0,
  voterAnswers: {},
  startedTracked: false,
  completedTracked: false,
};

function loadQuizState(): PersistedQuizState {
  if (typeof window === 'undefined') return QUIZ_DEFAULTS;
  try {
    const raw = window.localStorage.getItem(QUIZ_STORAGE_KEY);
    if (!raw) return QUIZ_DEFAULTS;
    const parsed = JSON.parse(raw) as Partial<PersistedQuizState>;
    const step =
      typeof parsed.step === 'number' && parsed.step >= 0 && parsed.step <= 3
        ? parsed.step
        : 0;
    const persistedMax =
      typeof parsed.maxVisitedStep === 'number' && parsed.maxVisitedStep >= 0 && parsed.maxVisitedStep <= 3
        ? parsed.maxVisitedStep
        : step;
    return {
      started: typeof parsed.started === 'boolean' ? parsed.started : false,
      districtPicked:
        typeof parsed.districtPicked === 'boolean' ? parsed.districtPicked : false,
      voterDistrict:
        typeof parsed.voterDistrict === 'number' && parsed.voterDistrict >= 1 && parsed.voterDistrict <= 13
          ? parsed.voterDistrict
          : null,
      step,
      maxVisitedStep: Math.max(persistedMax, step),
      voterAnswers:
        parsed.voterAnswers && typeof parsed.voterAnswers === 'object'
          ? (parsed.voterAnswers as AnswerMap)
          : {},
      startedTracked:
        typeof parsed.startedTracked === 'boolean' ? parsed.startedTracked : false,
      completedTracked:
        typeof parsed.completedTracked === 'boolean' ? parsed.completedTracked : false,
    };
  } catch {
    return QUIZ_DEFAULTS;
  }
}

function saveQuizState(s: PersistedQuizState): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(QUIZ_STORAGE_KEY, JSON.stringify(s));
  } catch {
    /* private mode or quota exceeded — quiz still works, just won't survive a refresh */
  }
}

function clearQuizState(): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.removeItem(QUIZ_STORAGE_KEY);
  } catch {
    /* ignore */
  }
}
import { encodeShareState, isShareEnabled } from '../../lib/quizShareUrl';
import {
  buildProjection as buildProjectionShared,
  projectAnswers as projectAnswersShared,
} from '../../lib/quizProjection';

// ---- Types --------------------------------------------------
type MPPos = QuizMP & { x: number; y: number };

// ---- Brand tokens (self-contained) -------------------------
const B = {
  red: '#FF1349',
  redDark: '#CF0A2C',
  redLight: '#FFE5ED',
  ink: '#222222',
  muted: '#929292',
  border: '#D8D8D8',
  borderAlt: '#CECECE',
  bg: '#F3F3F3',
  card: '#FFFFFF',
  growthCol: '#D4830A',
  sustainCol: '#1A7A4A',
  stateCol: '#1A4FA0',
  marketCol: '#8B2FC9',
  gold: '#E8B800',
  PL: '#CF1B2B',
  PN: '#003DA5',
  AD: '#2E8B4E',
  IND: '#5D4037',
  MOM: '#D17A00',
  AM: '#0A8F8F',
  IMP: '#8B2FC9',
  PLbg: '#fef2f2',
  PNbg: '#eff4ff',
  ADbg: '#f0f7f1',
  INDbg: '#f5f0ee',
  MOMbg: '#fdf3e6',
  AMbg: '#e6f5f5',
  IMPbg: '#f4ecfb',
};

const FONT_IMPORT = `@import url('https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;600;700;800;900&family=PT+Serif:ital,wght@0,400;0,700;1,400&display=swap');`;

// ---- Helpers (parameterised — no module-level constants) ---

// ---- Compass projection ----------------------------------------
//
// Intent-driven projection. The two compass axes are designed
// editorially:
//
//   X (GS): Stewardship (-1) ↔ Transformation (+1)
//   Y (PA): Self-reliance (-1) ↔ Collective bond (+1)
//
// Every question's seed weight (`growth` or `state`) carries a sign:
// positive means agreement pulls toward the + pole, negative toward
// the - pole, zero means the question is neutral on this axis and
// doesn't contribute. The 5-point reply scale maps 1→-1, 2→-0.5,
// 3→0, 4→+0.5, 5→+1.
//
// For each axis: position = mean of (n × sign(weight)) over questions
// the respondent answered on that axis. Result is in [-1, +1].
//
// After projecting every MP and the voter, rescale so the most-extreme
// MP position sits at ±0.85 of the plot edge — keeps the design
// intent visible while using the available canvas. The voter is
// rescaled by the same factor (so an extreme voter can still drift
// past ±0.85 — that's deliberate; their dot is supposed to land
// where their answers say it lands).

interface PoleLabels {
  xPos: string;
  xNeg: string;
  yPos: string;
  yNeg: string;
}

interface Projection {
  mpPos: Record<string, { x: number; y: number }>;
  voterPos: { x: number; y: number };
  poles: PoleLabels;
}

const FIXED_POLES: PoleLabels = {
  xPos: 'Transformation',
  xNeg: 'Stewardship',
  yPos: 'Collective bond',
  yNeg: 'Self-reliance',
};

// The compass projection math lives in src/lib/quizProjection.ts so
// the OG image endpoint can run the same algorithm server-side and
// the share-card dot positions match what the user sees on the page
// exactly. buildProjection here just augments the shared result with
// FIXED_POLES for the rendering layer.
function buildProjection(
  questions: QuizQuestion[],
  answers: AnswersByMP,
  voterAnswers: AnswerMap,
): Projection {
  return { ...buildProjectionShared(questions, answers, voterAnswers), poles: FIXED_POLES };
}

/** Symmetric match: only questions that BOTH the voter and the MP have
 *  answered count toward the score. */
function matchPct(
  questions: QuizQuestion[],
  answers: AnswersByMP,
  voterAnswers: AnswerMap,
  mpId: string,
): number {
  let dist = 0;
  let count = 0;
  const mpAnswers = answers[mpId] ?? {};
  questions.forEach((q) => {
    const v = voterAnswers[q.id];
    const m = mpAnswers[q.id];
    if (v != null && m != null) {
      dist += Math.abs(v - m);
      count++;
    }
  });
  return count ? Math.round((1 - dist / (count * 4)) * 100) : 0;
}

/** Computes the structured `quiz-completed` payload from the user's
 *  in-progress quiz state. Lives at module scope so the QuizRunner's
 *  `go()` callback can call it without re-binding closures.
 *
 *  Skips MPs that have zero shared answers with the voter (so their
 *  matchPct of 0 doesn't pollute the bottom-5 list with non-responders). */
function computeQuizCompletionPayload(
  data: QuizData,
  voterAnswers: AnswerMap,
  voterDistrict: number | null,
): QuizCompletionPayload {
  const proj = projectAnswersShared(data.questions, voterAnswers);

  const ranked = data.mps
    .map((mp) => {
      const sharedAnswered = data.questions.filter(
        (q) => voterAnswers[q.id] != null && (data.answers[mp.id]?.[q.id] != null),
      ).length;
      return {
        mp,
        score: matchPct(data.questions, data.answers, voterAnswers, mp.id),
        sharedAnswered,
      };
    })
    .filter((r) => r.sharedAnswered > 0)
    .sort((a, b) => b.score - a.score);

  const toIdScore = (r: { mp: QuizMP; score: number }) => ({
    id: Number(r.mp.id),
    score: r.score,
  });
  const topMatches = ranked.slice(0, 5).map(toIdScore).filter((m) => Number.isFinite(m.id));
  const bottomMatches = ranked
    .slice(-5)
    .reverse()
    .map(toIdScore)
    .filter((m) => Number.isFinite(m.id));

  const partyAverages: Record<string, number> = {};
  const partyKeys = new Set(ranked.map((r) => r.mp.party));
  for (const p of partyKeys) {
    const ofParty = ranked.filter((r) => r.mp.party === p);
    if (ofParty.length > 0) {
      const sum = ofParty.reduce((acc, r) => acc + r.score, 0);
      partyAverages[p] = Math.round(sum / ofParty.length);
    }
  }

  return {
    district: voterDistrict,
    compass: { x: Number(proj.x.toFixed(3)), y: Number(proj.y.toFixed(3)) },
    topMatches,
    bottomMatches,
    partyAverages,
    questionsAnswered: Object.keys(voterAnswers).length,
    totalQuestions: data.questions.length,
    answers: voterAnswers,
  };
}

const initials = (name: string) =>
  name
    .split(' ')
    .map((w) => w[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();

const SCORE_COLS = ['#C0392B', '#D4830A', '#888', '#27AE60', '#1A7A4A'];
const SCORE_LABELS = ['Strongly Disagree', 'Disagree', 'Neutral', 'Agree', 'Strongly Agree'];
const scoreCol = (v: number) => SCORE_COLS[v - 1] || '#ccc';
const scoreLabel = (v: number) => SCORE_LABELS[v - 1] || '';

const PARTY_COL: Record<string, string> = { PL: B.PL, PN: B.PN, AD: B.AD, IND: B.IND, MOM: B.MOM, AM: B.AM, IMP: B.IMP };
const PARTY_BG: Record<string, string> = { PL: B.PLbg, PN: B.PNbg, AD: B.ADbg, IND: B.INDbg, MOM: B.MOMbg, AM: B.AMbg, IMP: B.IMPbg };
const partyCol = (p: string) => PARTY_COL[p] || '#666';
const partyBg = (p: string) => PARTY_BG[p] || '#f5f5f5';

// ---- Global CSS (injected on mount) ------------------------
const CSS = `
  ${FONT_IMPORT}

  .lm { font-family: 'PT Serif', Georgia, serif; background: ${B.bg}; color: ${B.ink}; }
  .lm * { box-sizing: border-box; }
  .lm h1, .lm h2, .lm p, .lm button, .lm dl, .lm dd, .lm dt, .lm ul, .lm li, .lm table { margin: 0; padding: 0; }

  @keyframes lm-fadeUp { from { opacity: 0; transform: translateY(16px); } to { opacity: 1; transform: translateY(0); } }
  @keyframes lm-barIn  { from { width: 0; } }

  .lm .fu  { animation: lm-fadeUp 0.4s ease both; }
  .lm .fu1 { animation-delay: .06s }
  .lm .fu2 { animation-delay: .12s }
  .lm .fu3 { animation-delay: .18s }
  .lm .fu4 { animation-delay: .24s }
  .lm .fu5 { animation-delay: .30s }

  .lm .q-card { transition: border-color .2s, background .2s, box-shadow .2s; }
  .lm .q-card:hover { box-shadow: 0 3px 16px rgba(0,0,0,0.055); }
  .lm .q-card.done  { border-color: ${B.red}4D !important; background: #fff !important; }

  .lm .ans {
    cursor: pointer; transition: all .15s;
    border: 2px solid ${B.borderAlt}; border-radius: 6px;
    background: #fff; color: ${B.muted};
    font-family: 'Poppins', sans-serif; font-weight: 700; font-size: 11.5px;
    padding: 6px 13px; line-height: 1;
  }
  .lm .ans:hover:not(.on) { border-color: ${B.ink}; color: ${B.ink}; }

  .lm .bar-fill { animation: lm-barIn .5s ease both; transition: width .4s ease; }

  .lm .cta {
    background: ${B.red}; color: #fff; border: none; cursor: pointer;
    font-family: 'Poppins', sans-serif; font-weight: 800; font-size: 16px;
    padding: 15px 44px; border-radius: 8px; letter-spacing: .2px;
    transition: background .15s, transform .1s, box-shadow .15s;
  }
  .lm .cta:hover  { background: ${B.redDark}; transform: translateY(-2px); box-shadow: 0 8px 28px ${B.red}44; }
  .lm .cta:active { transform: translateY(0); }
  .lm .cta:disabled { background: ${B.borderAlt}; cursor: default; transform: none; box-shadow: none; }

  .lm .mc { transition: transform .12s; cursor: default; }
  .lm .mc:hover { transform: scale(1.22); }
  /* The YOU dot used to scale-pulse via keyframe animation. SVG group
     transforms scale around (0,0) of the local coord system, so each
     pulse cycle visibly drifted the dot 7% of (cx,cy) toward the
     bottom-right and back — readers correctly perceived this as
     position oscillation. The dot is now static and we lean on the
     gold fill + drop-shadow for visual emphasis. */

  /* Compact landing card — shown until the user opts in to the questionnaire */
  .lm-intro {
    padding: 40px 24px 44px;
    text-align: center;
  }
  .lm-intro__inner {
    max-width: 560px;
    margin: 0 auto;
  }
  .lm-intro__title {
    font-family: 'Poppins', sans-serif;
    font-weight: 900;
    font-size: clamp(28px, 4vw, 38px);
    line-height: 1.05;
    letter-spacing: -0.015em;
    text-transform: uppercase;
    color: ${B.ink};
    margin: 0 0 14px;
  }
  .lm-intro__lede {
    font-family: 'PT Serif', Georgia, serif;
    font-size: 15px;
    line-height: 1.65;
    color: ${B.muted};
    margin: 0 0 22px;
  }
  .lm-intro__stats {
    list-style: none;
    margin: 0 0 26px;
    padding: 14px 0;
    display: flex;
    justify-content: center;
    gap: 28px;
    flex-wrap: wrap;
    border-top: 1px solid ${B.border};
    border-bottom: 1px solid ${B.border};
  }
  .lm-intro__stats li {
    font-family: 'Poppins', sans-serif;
    font-size: 10px;
    letter-spacing: 1.5px;
    text-transform: uppercase;
    color: ${B.muted};
  }
  .lm-intro__stats strong {
    display: block;
    font-weight: 900;
    font-size: 22px;
    color: ${B.ink};
    margin-bottom: 3px;
    letter-spacing: -0.01em;
  }
  .lm-intro__hint {
    font-family: 'PT Serif', Georgia, serif;
    font-style: italic;
    font-size: 12px;
    color: ${B.muted};
    margin: 14px 0 0;
  }
  .lm-intro__coming {
    display: inline-block;
    background: ${B.ink};
    color: #fff;
    font-family: 'Poppins', sans-serif;
    font-weight: 800;
    font-size: 13px;
    letter-spacing: 3px;
    text-transform: uppercase;
    padding: 12px 32px;
    border-radius: 999px;
    box-shadow: 0 4px 18px rgba(0, 0, 0, 0.18);
  }

  /* In-section step indicator. Two columns:
       1. Scrolling steps row (.lm-steps)  — overflow-x: auto on mobile
       2. Refresh icon                     — pinned right, never scrolls
     Splitting them means the refresh stays in view even when the
     step labels run off the right edge on narrow viewports. */
  .lm-steps-wrap {
    display: flex; align-items: stretch;
    background: ${B.card};
    border-bottom: 1px solid ${B.border};
  }
  .lm-steps {
    display: flex; gap: 24px;
    padding: 14px 24px;
    overflow-x: auto;
    flex: 1 1 auto;
    min-width: 0; /* lets flexbox actually shrink + scroll inside */
  }
  .lm-step {
    background: none; border: 0; cursor: pointer;
    padding: 8px 2px;
    font-family: 'Poppins', sans-serif;
    font-weight: 700; font-size: 12px; letter-spacing: 1px; text-transform: uppercase;
    color: ${B.borderAlt};
    border-bottom: 2px solid transparent;
    white-space: nowrap;
    display: inline-flex; align-items: center; gap: 6px;
    transition: color .15s, border-color .15s;
  }
  .lm-step.is-current { color: ${B.red}; border-bottom-color: ${B.red}; }
  .lm-step.is-done    { color: ${B.muted}; }
  .lm-step.is-done:hover { color: ${B.ink}; }
  /* Visited but not current — like .is-done but visually intact so
     you can hop forward to where you were last. */
  .lm-step.is-visited { color: ${B.muted}; }
  .lm-step.is-visited:hover { color: ${B.ink}; }
  .lm-step.is-future  { cursor: default; }
  .lm-step__check     { color: ${B.muted}; font-weight: 900; font-size: 13px; }
  /* Refresh icon — replaces the old "Start over" text link. Lives in
     the wrapper, NOT inside the scrolling .lm-steps row, so it stays
     pinned to the right of the step bar regardless of how many step
     labels overflow on mobile. */
  .lm-restart-icon {
    background: none; border: 0; cursor: pointer;
    align-self: center;
    flex-shrink: 0;
    margin: 0 12px;
    padding: 8px;
    border-radius: 50%;
    color: ${B.borderAlt};
    display: inline-flex; align-items: center; justify-content: center;
    transition: color .15s, background .15s;
  }
  .lm-restart-icon:hover { color: ${B.ink}; background: ${B.bg}; }

  /* Floating share button — sits at the right end of the district
     filter row. Larger + brand red + shadow so it reads as a tappable
     action, not just a tag. */
  .lm-share-btn {
    background: ${B.red}; border: 0; cursor: pointer;
    width: 44px; height: 44px; border-radius: 50%;
    order: -1; /* sits at the LEFT of the filter row */
    margin-right: 14px;
    display: inline-flex; align-items: center; justify-content: center;
    color: #fff;
    transition: transform .12s ease, box-shadow .15s ease, background .15s;
    box-shadow: 0 4px 14px rgba(255, 19, 73, 0.35), 0 1px 3px rgba(0,0,0,0.12);
  }
  .lm-share-btn:hover:not(:disabled) { background: ${B.redDark}; transform: translateY(-2px); box-shadow: 0 6px 18px rgba(255, 19, 73, 0.42), 0 1px 3px rgba(0,0,0,0.12); }
  .lm-share-btn:active { transform: translateY(0); }
  .lm-share-btn:disabled { opacity: 0.65; cursor: wait; }

  @media (max-width: 560px) {
    /* Stack on mobile: scrolling steps row on top, refresh icon on its
       own row below right-aligned so it doesn't compete with the step
       labels for the cramped horizontal space. */
    .lm-steps-wrap { flex-direction: column; align-items: stretch; }
    .lm-steps { gap: 16px; padding: 12px 16px; }
    .lm-step { font-size: 11px; }
    .lm-restart-icon {
      align-self: flex-end;
      margin: 0 12px 8px;
      padding: 6px;
    }
    .lm-share-btn { width: 40px; height: 40px; }
    .lm .cpsvg svg { width: 320px !important; height: 320px !important; }
  }
`;

// ---- UI atoms ----------------------------------------------
function StepIndicator({
  step,
  maxVisitedStep,
  onNav,
  onRestart,
}: {
  step: number;
  maxVisitedStep: number;
  onNav: (s: number) => void;
  onRestart?: () => void;
}) {
  const labels = ['Your Views', 'Top Matches', 'Compass', 'Full Matrix'];
  return (
    <div className="lm-steps-wrap">
      <nav className="lm-steps" aria-label="Quiz progress">
        {labels.map((label, i) => {
          const isCurrent = i === step;
          const isDone = i < step;
          // Locked until the visitor has actually reached it. Once
          // reached, every step ≤ maxVisitedStep is navigable in both
          // directions — so you can hop back to Views, then forward
          // to Matrix again from this row without retaking the quiz.
          const isLocked = i > maxVisitedStep;
          const cls = `lm-step ${isCurrent ? 'is-current' : isDone ? 'is-done' : isLocked ? 'is-future' : 'is-visited'}`;
          return (
            <button
              key={label}
              type="button"
              className={cls}
              onClick={() => {
                if (!isLocked) onNav(i);
              }}
              disabled={isLocked}
              aria-current={isCurrent ? 'step' : undefined}
            >
              {isDone && (
                <span className="lm-step__check" aria-hidden="true">
                  ✓
                </span>
              )}
              {label}
            </button>
          );
        })}
      </nav>
      {onRestart && (
        <button
          type="button"
          onClick={onRestart}
          className="lm-restart-icon"
          title="Clear your answers and start the quiz from scratch"
          aria-label="Start over"
        >
          <RefreshGlyph />
        </button>
      )}
    </div>
  );
}

function RefreshGlyph() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <polyline points="23 4 23 10 17 10" />
      <polyline points="1 20 1 14 7 14" />
      <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
    </svg>
  );
}

// ---- Screen 0a: Compact intro card -------------------------
// Visibility is controlled exclusively by the `quiz` feature flag in
// /admin/flags — set to 'coming_soon' to hide the whole module behind
// the frosted overlay, or 'live' to expose the start button below.
function QuizIntro({ onStart, data }: { onStart: () => void; data: QuizData }) {
  return (
    <div className="lm-intro fu">
      <div className="lm-intro__inner">
        <h2 className="lm-intro__title">
          See How <span style={{ color: B.red }}>You</span> Compare
        </h2>
        <p className="lm-intro__lede">
          {data.questions.length} short questions about how you view politics
          and the world around us. Answer them yourself, then see which of
          Malta&apos;s MPs genuinely think like you.
        </p>
        <ul className="lm-intro__stats">
          <li>
            <strong>{data.questions.length}</strong>
            questions
          </li>
          <li>
            <strong>~{Math.max(2, Math.round(data.questions.length * 0.25))}</strong>
            minutes
          </li>
          <li>
            <strong>{data.mps.length}</strong>
            MPs to match
          </li>
        </ul>
        <button type="button" className="cta" onClick={onStart}>
          Start Answering →
        </button>
        <p className="lm-intro__hint">
          No sign-up. Your answers stay on your device.
        </p>
      </div>
    </div>
  );
}

// ---- Screen 0b: District selection -------------------------
const MALTA_DISTRICTS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13];

// District filter helpers — Maltese rules let candidates contest in
// up to two districts (`district` + `district_2`), and the filter UI
// treats the chips as "any of these districts", so a multi-district
// MP must appear under both chips.
// Defensive number coercion — Postgres INTEGER columns *should* come
// back as JS numbers via @neondatabase/serverless, but if anything in
// the pipeline coerces to a string (e.g. JSON parser settings, an
// upstream import that wrote a string), strict-equality below silently
// breaks. Forcing Number() here makes the filter robust to type drift.
function asDistrictNumber(v: unknown): number | null {
  if (v == null || v === '') return null;
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n) ? n : null;
}

function mpInDistrict(
  mp: { district: number | null; district_2: number | null },
  filter: 'ALL' | number,
): boolean {
  if (filter === 'ALL') return true;
  const target = asDistrictNumber(filter);
  if (target == null) return false;
  return asDistrictNumber(mp.district) === target ||
    asDistrictNumber(mp.district_2) === target;
}

function availableDistrictsFor(
  mps: { district: number | null; district_2: number | null }[],
): number[] {
  const set = new Set<number>();
  for (const m of mps) {
    const d1 = asDistrictNumber(m.district);
    const d2 = asDistrictNumber(m.district_2);
    if (d1 != null) set.add(d1);
    if (d2 != null) set.add(d2);
  }
  return [...set].sort((a, b) => a - b);
}

function DistrictPicker({
  onPick,
}: {
  onPick: (district: number | null) => void;
}) {
  return (
    <div className="lm-intro fu">
      <div className="lm-intro__inner" style={{ maxWidth: 620 }}>
        <h2 className="lm-intro__title">
          Which District <span style={{ color: B.red }}>Do You Vote In?</span>
        </h2>
        <p className="lm-intro__lede">
          We&apos;ll show your match nationally and locally, so you can also see
          who in your own district lines up with how you think.
        </p>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(64px, 1fr))',
            gap: 8,
            margin: '14px 0 22px',
          }}
        >
          {MALTA_DISTRICTS.map((d) => (
            <button
              key={d}
              type="button"
              onClick={() => onPick(d)}
              style={{
                padding: '14px 0',
                borderRadius: 8,
                border: `2px solid ${B.borderAlt}`,
                background: '#fff',
                color: B.ink,
                fontFamily: "'Poppins', sans-serif",
                fontWeight: 800,
                fontSize: 16,
                cursor: 'pointer',
                transition: 'border-color .15s, background .15s, transform .1s',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = B.red;
                e.currentTarget.style.color = B.red;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = B.borderAlt;
                e.currentTarget.style.color = B.ink;
              }}
            >
              {d}
            </button>
          ))}
        </div>
        <button
          type="button"
          onClick={() => onPick(null)}
          style={{
            background: 'transparent',
            border: 0,
            color: B.muted,
            fontFamily: "'Poppins', sans-serif",
            fontWeight: 700,
            fontSize: 12,
            letterSpacing: 0.5,
            textDecoration: 'underline',
            cursor: 'pointer',
            padding: '6px 12px',
          }}
        >
          Skip — I&apos;m not sure / prefer not to say
        </button>
      </div>
    </div>
  );
}

// ---- Screen 0: Questionnaire -------------------------------
function Questionnaire({
  data,
  answers,
  onAnswersChange,
  onComplete,
}: {
  data: QuizData;
  answers: AnswerMap;
  onAnswersChange: (a: AnswerMap) => void;
  onComplete: () => void;
}) {
  const answered = Object.keys(answers).length;
  const pct = data.questions.length
    ? Math.round((answered / data.questions.length) * 100)
    : 0;
  const minToContinue = Math.max(1, Math.ceil(data.questions.length / 2));
  const canGo = answered >= minToContinue;

  const set = (id: string, v: number) => onAnswersChange({ ...answers, [id]: v });

  return (
    <div style={{ maxWidth: 700, margin: '0 auto', padding: '28px 20px 56px' }}>
      <div className="fu" style={{ marginBottom: 24 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 7 }}>
          <span
            style={{
              fontFamily: "'Poppins', sans-serif",
              fontSize: 11,
              fontWeight: 700,
              color: B.muted,
              letterSpacing: 1,
              textTransform: 'uppercase',
            }}
          >
            Progress
          </span>
          <span
            style={{
              fontFamily: "'Poppins', sans-serif",
              fontSize: 11,
              fontWeight: 700,
              color: canGo ? B.red : B.muted,
            }}
          >
            {answered} of {data.questions.length}
          </span>
        </div>
        <div style={{ background: B.border, borderRadius: 99, height: 5, overflow: 'hidden' }}>
          <div
            className="bar-fill"
            style={{ width: `${pct}%`, height: '100%', background: B.red, borderRadius: 99 }}
          />
        </div>
      </div>

      {/* Single uninterrupted list — no editorial section dividers. The
          underlying axis tag is preserved for the compass math but is not
          surfaced to the reader, so the questions don't pre-frame what
          ideological camp they're meant to slot into. */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 11, marginBottom: 36 }}>
        {data.questions.map((q, i) => (
          <QCard
            key={q.id}
            q={q}
            num={i + 1}
            val={answers[q.id]}
            onChange={(v: number) => set(q.id, v)}
          />
        ))}
      </div>

      <div style={{ textAlign: 'center' }}>
        {!canGo && (
          <p
            style={{
              fontFamily: "'Poppins', sans-serif",
              fontSize: 12,
              color: B.muted,
              marginBottom: 12,
            }}
          >
            Answer at least {minToContinue - answered} more question
            {minToContinue - answered !== 1 ? 's' : ''} to continue
          </p>
        )}
        <button className="cta" disabled={!canGo} onClick={() => onComplete()}>
          See My Results →
        </button>
      </div>
    </div>
  );
}

function QCard({
  q,
  num,
  val,
  onChange,
}: {
  q: QuizQuestion;
  num: number;
  val: number | undefined;
  onChange: (v: number) => void;
}) {
  const opts: [number, string][] = [
    [1, 'Strongly Disagree'],
    [2, 'Disagree'],
    [3, 'Neutral'],
    [4, 'Agree'],
    [5, 'Strongly Agree'],
  ];
  return (
    <div
      className={`q-card${val ? ' done' : ''}`}
      style={{
        border: `1.5px solid ${B.border}`,
        borderRadius: 10,
        padding: '17px 19px',
        background: val ? '#fff' : '#fafafa',
      }}
    >
      <div
        style={{ display: 'flex', gap: 13, marginBottom: 13, alignItems: 'flex-start' }}
      >
        <span
          style={{
            fontFamily: "'Poppins', sans-serif",
            fontWeight: 900,
            fontSize: 22,
            color: val ? B.red : B.border,
            lineHeight: 1,
            minWidth: 34,
            flexShrink: 0,
          }}
        >
          {String(num).padStart(2, '0')}
        </span>
        <p
          style={{
            margin: 0,
            fontSize: 13.5,
            lineHeight: 1.72,
            color: B.ink,
            fontWeight: 400,
            paddingTop: 2,
          }}
        >
          {q.text}
        </p>
      </div>
      <div style={{ display: 'flex', gap: 7, paddingLeft: 47, flexWrap: 'wrap' }}>
        {opts.map(([v, label]) => (
          <button
            key={v}
            type="button"
            className={`ans${val === v ? ' on' : ''}`}
            onClick={() => onChange(v)}
            style={{
              borderColor: val === v ? scoreCol(v) : B.borderAlt,
              background: val === v ? scoreCol(v) : '#fff',
              color: val === v ? '#fff' : B.muted,
            }}
          >
            {label}
          </button>
        ))}
      </div>
    </div>
  );
}

// ---- Screen 2: Top matches ---------------------------------
const PAGE_SIZE = 5;
const PAGE_INCREMENT = 4;

function TopMatches({
  data,
  voterAnswers,
  voterDistrict,
  onNext,
}: {
  data: QuizData;
  voterAnswers: AnswerMap;
  voterDistrict: number | null;
  onNext: () => void;
}) {
  // Default to the user's picked district so they see candidates from
  // their own race first. The chip strip below lets them switch to ALL
  // (or any other district) if they want a wider view.
  const [districtFilter, setDistrictFilter] = useState<'ALL' | number>(
    voterDistrict ?? 'ALL',
  );
  const [topShown, setTopShown] = useState(PAGE_SIZE);
  const [bottomShown, setBottomShown] = useState(PAGE_SIZE);

  const availableDistricts = availableDistrictsFor(data.mps);

  const ranked = data.mps
    .filter((mp) => mpInDistrict(mp, districtFilter))
    .map((mp) => ({
      ...mp,
      score: matchPct(data.questions, data.answers, voterAnswers, mp.id),
    }))
    .sort((a, b) => b.score - a.score);

  const topList = ranked.slice(0, topShown);
  // Least like you: pull from the bottom of the ranking AFTER the top list,
  // so the same person is never shown in both sections. Reversed so the
  // worst match appears first. As the user paginates "Show more" on top,
  // the bottom pool shrinks accordingly.
  const bottomPool = ranked.slice(topShown);
  const bottomList = [...bottomPool]
    .reverse()
    .slice(0, bottomShown);

  const partyAvgs: Record<string, number> = {};
  (['PL', 'PN', 'AD', 'IND', 'MOM', 'AM', 'IMP'] as const).forEach((p) => {
    const vals = ranked.filter((m) => m.party === p).map((m) => m.score);
    if (vals.length)
      partyAvgs[p] = Math.round(vals.reduce((a, b) => a + b, 0) / vals.length);
  });
  const sortedP = Object.entries(partyAvgs).sort((a, b) => b[1] - a[1]);

  return (
    <div style={{ maxWidth: 660, margin: '0 auto', padding: '36px 20px 64px' }}>
      <div className="fu" style={{ marginBottom: 28 }}>
        <h2
          style={{
            fontFamily: "'Poppins', sans-serif",
            fontWeight: 900,
            fontSize: 32,
            color: B.ink,
            lineHeight: 1.1,
            marginBottom: 10,
            textTransform: 'uppercase',
          }}
        >
          Your Closest
          <br />
          <span style={{ color: B.red }}>Political Matches</span>
        </h2>
        <p style={{ fontSize: 14, color: B.muted, lineHeight: 1.8 }}>
          Based on your answers, these are the MPs whose worldview aligns most
          with yours.
        </p>
      </div>

      {availableDistricts.length > 0 && (
        <div
          className="fu fu1"
          style={{
            display: 'flex',
            gap: 6,
            flexWrap: 'wrap',
            marginBottom: 22,
            alignItems: 'center',
          }}
        >
          <span
            style={{
              fontFamily: "'Poppins', sans-serif",
              fontWeight: 700,
              fontSize: 10.5,
              letterSpacing: 1.4,
              textTransform: 'uppercase',
              color: B.muted,
              marginRight: 4,
            }}
          >
            District
          </span>
          {(['ALL', ...MALTA_DISTRICTS] as ('ALL' | number)[]).map((d) => {
            const isYours = voterDistrict != null && d === voterDistrict;
            return (
              <button
                key={String(d)}
                type="button"
                onClick={() => {
                  setDistrictFilter(d);
                  setTopShown(PAGE_SIZE);
                  setBottomShown(PAGE_SIZE);
                }}
                style={{
                  padding: '5px 12px',
                  borderRadius: 6,
                  border: '2px solid',
                  borderColor:
                    districtFilter === d
                      ? B.ink
                      : isYours
                        ? B.gold
                        : B.borderAlt,
                  background:
                    districtFilter === d ? B.ink : isYours ? B.gold : 'transparent',
                  color:
                    districtFilter === d ? '#fff' : isYours ? B.ink : B.muted,
                  fontFamily: "'Poppins', sans-serif",
                  fontWeight: 700,
                  fontSize: 12,
                  cursor: 'pointer',
                }}
              >
                {d === 'ALL' ? 'All' : `D${d}`}
                {isYours && (
                  <span style={{ marginLeft: 4, fontSize: 9, opacity: 0.8 }}>
                    (yours)
                  </span>
                )}
              </button>
            );
          })}
        </div>
      )}

      <div
        className="fu fu1"
        style={{ display: 'flex', gap: 10, marginBottom: 28, flexWrap: 'wrap' }}
      >
        {sortedP.map(([p, avg], i) => (
          <div
            key={p}
            style={{
              flex: '1 1 110px',
              borderRadius: 10,
              padding: '14px 18px',
              textAlign: 'center',
              border: `2px solid ${partyCol(p)}`,
              background: i === 0 ? partyCol(p) : partyBg(p),
              color: i === 0 ? '#fff' : partyCol(p),
              transform: i === 0 ? 'translateY(-3px)' : 'none',
              boxShadow: i === 0 ? `0 8px 28px ${partyCol(p)}44` : 'none',
            }}
          >
            {i === 0 && (
              <div
                style={{
                  fontFamily: "'Poppins', sans-serif",
                  fontSize: 8,
                  fontWeight: 800,
                  letterSpacing: 2,
                  opacity: 0.75,
                  marginBottom: 3,
                  textTransform: 'uppercase',
                }}
              >
                Best Fit
              </div>
            )}
            <div style={{ fontFamily: "'Poppins', sans-serif", fontWeight: 900, fontSize: 22 }}>
              {p}
            </div>
            <div
              style={{
                fontFamily: "'Poppins', sans-serif",
                fontWeight: 900,
                fontSize: 30,
                lineHeight: 1,
              }}
            >
              {avg}%
            </div>
            <div style={{ fontSize: 10, opacity: 0.7, marginTop: 2 }}>avg match</div>
          </div>
        ))}
      </div>

      <MatchList
        list={topList}
        emphasiseFirst
        emptyText={
          districtFilter === 'ALL'
            ? 'No candidates match these filters.'
            : `No candidates from District ${districtFilter} have answered the quiz yet. Click "All" above to see nationwide matches.`
        }
      />
      {topShown < ranked.length && (
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <button
            type="button"
            onClick={() =>
              setTopShown((n) => Math.min(n + PAGE_INCREMENT, ranked.length))
            }
            style={{
              background: 'transparent',
              border: `2px solid ${B.border}`,
              borderRadius: 6,
              color: B.ink,
              fontFamily: "'Poppins', sans-serif",
              fontWeight: 700,
              fontSize: 12,
              padding: '8px 16px',
              cursor: 'pointer',
              letterSpacing: 0.5,
            }}
          >
            Show {Math.min(PAGE_INCREMENT, ranked.length - topShown)} more →
          </button>
        </div>
      )}

      {bottomList.length > 0 && (
        <>
          <h3
            style={{
              fontFamily: "'Poppins', sans-serif",
              fontWeight: 900,
              fontSize: 24,
              color: B.ink,
              lineHeight: 1.1,
              margin: '40px 0 6px',
              textTransform: 'uppercase',
            }}
          >
            Least Like <span style={{ color: B.red }}>You</span>
          </h3>
          <p
            style={{
              fontSize: 13,
              color: B.muted,
              lineHeight: 1.7,
              margin: '0 0 20px',
            }}
          >
            The candidates whose answers diverge most from yours.
          </p>
          <MatchList list={bottomList} />
          {bottomShown < bottomPool.length && (
            <div style={{ textAlign: 'center', marginBottom: 32 }}>
              <button
                type="button"
                onClick={() =>
                  setBottomShown((n) =>
                    Math.min(n + PAGE_INCREMENT, bottomPool.length),
                  )
                }
                style={{
                  background: 'transparent',
                  border: `2px solid ${B.border}`,
                  borderRadius: 6,
                  color: B.ink,
                  fontFamily: "'Poppins', sans-serif",
                  fontWeight: 700,
                  fontSize: 12,
                  padding: '8px 16px',
                  cursor: 'pointer',
                  letterSpacing: 0.5,
                }}
              >
                Show {Math.min(PAGE_INCREMENT, bottomPool.length - bottomShown)} more →
              </button>
            </div>
          )}
        </>
      )}

      <div style={{ textAlign: 'center', marginTop: 32 }}>
        <button className="cta" onClick={onNext}>
          See the Political Compass →
        </button>
      </div>
    </div>
  );
}

// Renders a numbered list of MP cards with a percentage match score.
// Used by both the top-matches and "least like you" sections so they
// share visual treatment.
function MatchList({
  list,
  emphasiseFirst = false,
  emptyText,
}: {
  list: (QuizMP & { score: number })[];
  emphasiseFirst?: boolean;
  emptyText?: string;
}) {
  if (list.length === 0) {
    return (
      <p
        style={{
          fontFamily: "'Poppins', sans-serif",
          fontSize: 13,
          color: B.muted,
          textAlign: 'center',
          margin: '24px 0',
        }}
      >
        {emptyText ?? 'No candidates to show.'}
      </p>
    );
  }
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 12 }}>
      {list.map((mp, i) => {
        const featured = emphasiseFirst && i === 0;
        return (
          <div
            key={mp.id}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 14,
              borderRadius: 10,
              padding: '14px 18px',
              background: featured ? partyCol(mp.party) : '#fff',
              border: `1.5px solid ${featured ? partyCol(mp.party) : B.border}`,
              boxShadow: featured ? `0 6px 24px ${partyCol(mp.party)}44` : 'none',
            }}
          >
            <div
              style={{
                fontFamily: "'Poppins', sans-serif",
                fontWeight: 900,
                fontSize: 26,
                color: featured ? 'rgba(255,255,255,0.3)' : B.border,
                minWidth: 38,
              }}
            >
              #{i + 1}
            </div>
            <div
              style={{
                width: 44,
                height: 44,
                borderRadius: '50%',
                flexShrink: 0,
                background: featured ? 'rgba(255,255,255,0.2)' : partyBg(mp.party),
                color: featured ? '#fff' : partyCol(mp.party),
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontFamily: "'Poppins', sans-serif",
                fontWeight: 900,
                fontSize: 13,
                border: `2px solid ${featured ? 'rgba(255,255,255,0.3)' : `${partyCol(mp.party)}33`}`,
              }}
            >
              {initials(mp.name)}
            </div>
            <div style={{ flex: 1 }}>
              <div
                style={{
                  fontFamily: "'Poppins', sans-serif",
                  fontWeight: 700,
                  fontSize: 15,
                  color: featured ? '#fff' : B.ink,
                }}
              >
                {mp.name}
              </div>
              <div
                style={{
                  fontSize: 12,
                  color: featured ? 'rgba(255,255,255,0.7)' : partyCol(mp.party),
                  fontWeight: 600,
                }}
              >
                {mp.party}
                {mp.district != null
                  ? ` · D${mp.district}${mp.district_2 != null ? ` + ${mp.district_2}` : ''}`
                  : ''}
                {mp.role ? ` · ${mp.role}` : ''}
              </div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div
                style={{
                  fontFamily: "'Poppins', sans-serif",
                  fontWeight: 900,
                  fontSize: 30,
                  color: featured ? '#fff' : B.ink,
                  lineHeight: 1,
                }}
              >
                {mp.score}%
              </div>
              <div
                style={{
                  fontSize: 10,
                  color: featured ? 'rgba(255,255,255,0.6)' : B.muted,
                }}
              >
                match
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ---- Screen 3: Compass -------------------------------------
function Compass({
  data,
  voterAnswers,
  voterDistrict,
  onNext,
}: {
  data: QuizData;
  voterAnswers: AnswerMap;
  voterDistrict: number | null;
  onNext: () => void;
}) {
  const [tip, setTip] = useState<MPPos | null>(null);
  // Default to the user's picked district so they see candidates from
  // their own race plotted prominently. They can switch to ALL to see
  // every responding candidate clustered.
  const [districtFilter, setDistrictFilter] = useState<'ALL' | number>(
    voterDistrict ?? 'ALL',
  );
  // SVG uses viewBox + width 100% so the compass fills the section's
  // container. Internal coordinates stay 0..SZ. Padding is generous
  // enough that "Stewardship" / "Transformation" pole labels fit
  // outside the plot area without clipping the viewBox.
  const SZ = 720;
  const P = 110;
  const INN = SZ - P * 2;

  // PCA over MP × question matrix. Memoised so we don't redo eigen
  // iteration on every render (questions/answers/voterAnswers are
  // stable for the duration of a session).
  const projection = useMemo(
    () => buildProjection(data.questions, data.answers, voterAnswers),
    [data.questions, data.answers, voterAnswers],
  );

  const allMpPos: MPPos[] = data.mps
    .filter((mp) => projection.mpPos[mp.id] != null)
    .map((mp) => ({ ...mp, ...projection.mpPos[mp.id] }));
  const mpPos: MPPos[] = allMpPos.filter((mp) =>
    mpInDistrict(mp, districtFilter),
  );
  const noResponse = data.mps.filter(
    (mp) => Object.keys(data.answers[mp.id] ?? {}).length === 0,
  );

  const availableDistricts = availableDistrictsFor(data.mps);

  const vp = projection.voterPos;
  const gx = (v: number) => P + ((v + 1) / 2) * INN;
  const gy = (v: number) => P + ((1 - v) / 2) * INN;
  const poles = projection.poles;

  return (
    <div style={{ maxWidth: 920, margin: '0 auto', padding: '36px 20px 64px' }}>
      <div className="fu" style={{ marginBottom: 22 }}>
        <h2
          style={{
            fontFamily: "'Poppins', sans-serif",
            fontWeight: 900,
            fontSize: 32,
            color: B.ink,
            lineHeight: 1.1,
            marginBottom: 10,
            textTransform: 'uppercase',
          }}
        >
          Where Everyone
          <br />
          <span style={{ color: B.red }}>Lands</span>
        </h2>
        <p style={{ fontSize: 14, color: B.muted, lineHeight: 1.8 }}>
          Distance shows similarity. Politicians who answered like you cluster
          near your <strong style={{ color: B.gold }}>gold</strong> dot.
        </p>
      </div>

      {availableDistricts.length > 0 && (
        <div
          className="fu fu1"
          style={{
            display: 'flex',
            gap: 6,
            flexWrap: 'wrap',
            marginBottom: 16,
            alignItems: 'center',
          }}
        >
          <span
            style={{
              fontFamily: "'Poppins', sans-serif",
              fontWeight: 700,
              fontSize: 10.5,
              letterSpacing: 1.4,
              textTransform: 'uppercase',
              color: B.muted,
              marginRight: 4,
            }}
          >
            District
          </span>
          {(['ALL', ...MALTA_DISTRICTS] as ('ALL' | number)[]).map((d) => {
            const isYours = voterDistrict != null && d === voterDistrict;
            return (
              <button
                key={String(d)}
                type="button"
                onClick={() => setDistrictFilter(d)}
                style={{
                  padding: '5px 12px',
                  borderRadius: 6,
                  border: '2px solid',
                  borderColor:
                    districtFilter === d
                      ? B.ink
                      : isYours
                        ? B.gold
                        : B.borderAlt,
                  background:
                    districtFilter === d ? B.ink : isYours ? B.gold : 'transparent',
                  color:
                    districtFilter === d ? '#fff' : isYours ? B.ink : B.muted,
                  fontFamily: "'Poppins', sans-serif",
                  fontWeight: 700,
                  fontSize: 12,
                  cursor: 'pointer',
                }}
              >
                {d === 'ALL' ? 'All' : `D${d}`}
                {isYours && (
                  <span style={{ marginLeft: 4, fontSize: 9, opacity: 0.8 }}>
                    (yours)
                  </span>
                )}
              </button>
            );
          })}
        </div>
      )}

      <div className="fu fu2 cpsvg">
        <svg
          viewBox={`0 0 ${SZ} ${SZ}`}
          preserveAspectRatio="xMidYMid meet"
          style={{ display: 'block', width: '100%', height: 'auto' }}
        >
          {/* Card background */}
          <rect x={P} y={P} width={INN} height={INN} fill="#fafafa" stroke={B.border} strokeWidth={1} rx={6} />

          {/* Quadrant tints — four pastel corners. The tints are subtle
              enough to read as "compass quadrants" without dominating
              the data. */}
          <rect x={P}        y={P}        width={INN/2} height={INN/2} fill="#FFF1ED" opacity={0.7} />
          <rect x={P+INN/2}  y={P}        width={INN/2} height={INN/2} fill="#EAF4FF" opacity={0.7} />
          <rect x={P}        y={P+INN/2}  width={INN/2} height={INN/2} fill="#F3F4DE" opacity={0.7} />
          <rect x={P+INN/2}  y={P+INN/2}  width={INN/2} height={INN/2} fill="#EDF6EC" opacity={0.7} />

          {/* Subtle grid every 0.5 unit */}
          {[-0.5, 0, 0.5].map((v) => (
            <g key={v}>
              <line
                x1={gx(v)}
                y1={P}
                x2={gx(v)}
                y2={P + INN}
                stroke={v === 0 ? '#888' : '#ddd'}
                strokeWidth={v === 0 ? 1.4 : 0.8}
                strokeDasharray={v === 0 ? 'none' : '5,4'}
                opacity={v === 0 ? 0.6 : 0.7}
              />
              <line
                x1={P}
                y1={gy(v)}
                x2={P + INN}
                y2={gy(v)}
                stroke={v === 0 ? '#888' : '#ddd'}
                strokeWidth={v === 0 ? 1.4 : 0.8}
                strokeDasharray={v === 0 ? 'none' : '5,4'}
                opacity={v === 0 ? 0.6 : 0.7}
              />
            </g>
          ))}
          {(['PL', 'PN', 'AD', 'MOM', 'AM', 'IMP'] as const).map((party) => {
            const pts = mpPos.filter((m) => m.party === party);
            if (pts.length < 2) return null;
            const cx = pts.reduce((a, b) => a + b.x, 0) / pts.length;
            const cy = pts.reduce((a, b) => a + b.y, 0) / pts.length;
            const rx = Math.max(...pts.map((p) => Math.abs(p.x - cx))) + 0.12;
            const ry = Math.max(...pts.map((p) => Math.abs(p.y - cy))) + 0.12;
            return (
              <ellipse
                key={party}
                cx={gx(cx)}
                cy={gy(cy)}
                rx={rx * (INN / 2)}
                ry={ry * (INN / 2)}
                fill={partyCol(party)}
                fillOpacity=".06"
                stroke={partyCol(party)}
                strokeOpacity=".18"
                strokeWidth="1.5"
                strokeDasharray="6,4"
              />
            );
          })}

          {/* Axis pole labels — derived from PCA top contributors. Each
              pole shows the topic(s) of the question(s) that pull dots
              toward that pole. The Y axis uses dy="-6" / "+12" so the
              label sits above/below the plot edge cleanly. */}
          {poles.yPos && (
            <text
              x={gx(0)}
              y={P - 14}
              textAnchor="middle"
              fontSize="12"
              fontWeight="700"
              fill={B.ink}
              fontFamily="'Poppins', sans-serif"
            >
              {poles.yPos}
            </text>
          )}
          {poles.yNeg && (
            <text
              x={gx(0)}
              y={P + INN + 24}
              textAnchor="middle"
              fontSize="12"
              fontWeight="700"
              fill={B.ink}
              fontFamily="'Poppins', sans-serif"
            >
              {poles.yNeg}
            </text>
          )}
          {poles.xNeg && (
            <text
              x={P - 8}
              y={gy(0) + 4}
              textAnchor="end"
              fontSize="11"
              fontWeight="700"
              fill={B.ink}
              fontFamily="'Poppins', sans-serif"
            >
              {poles.xNeg}
            </text>
          )}
          {poles.xPos && (
            // Right-side pole used to be anchored at P+INN+12 with
            // textAnchor="start". "Transformation" overflowed the
            // viewBox and got clipped. Anchor at SZ-4 with
            // textAnchor="end" so the label is right-aligned to the
            // viewBox edge, font shrunk to 11 to keep clear of the
            // compass right edge at P+INN.
            <text
              x={SZ - 4}
              y={gy(0) + 4}
              textAnchor="end"
              fontSize="11"
              fontWeight="700"
              fill={B.ink}
              fontFamily="'Poppins', sans-serif"
            >
              {poles.xPos}
            </text>
          )}

          {mpPos.map((mp) => {
            const cx = gx(mp.x);
            const cy = gy(mp.y);
            const hov = tip?.id === mp.id;
            return (
              <g
                key={mp.id}
                style={{ cursor: 'pointer' }}
                onMouseEnter={() => setTip(mp)}
                onMouseLeave={() => setTip(null)}
                onTouchStart={() => setTip(tip?.id === mp.id ? null : mp)}
              >
                <circle
                  cx={cx}
                  cy={cy}
                  r={hov ? 17 : 13}
                  fill={partyCol(mp.party)}
                  stroke="#fff"
                  strokeWidth={hov ? 2.5 : 2}
                  style={{
                    filter: hov ? 'drop-shadow(0 3px 10px rgba(0,0,0,0.3))' : 'none',
                    transition: 'r .12s',
                  }}
                />
                <text
                  x={cx}
                  y={cy + 3.5}
                  textAnchor="middle"
                  fontSize="8"
                  fill="#fff"
                  fontWeight="700"
                  fontFamily="'Poppins', sans-serif"
                  pointerEvents="none"
                >
                  {initials(mp.name)}
                </text>
              </g>
            );
          })}
          <g>
            <circle
              cx={gx(vp.x)}
              cy={gy(vp.y)}
              r={17}
              fill={B.gold}
              stroke={B.ink}
              strokeWidth={2.5}
              style={{ filter: `drop-shadow(0 3px 14px ${B.gold}99)` }}
            />
            <text
              x={gx(vp.x)}
              y={gy(vp.y) + 3.5}
              textAnchor="middle"
              fontSize="8"
              fill={B.ink}
              fontWeight="800"
              fontFamily="'Poppins', sans-serif"
              pointerEvents="none"
            >
              YOU
            </text>
          </g>
          {tip &&
            (() => {
              const tx = gx(tip.x);
              const ty = gy(tip.y);
              const bx = tx > SZ - 195 ? tx - 190 : tx + 22;
              const by = ty > SZ - 100 ? ty - 96 : ty - 6;
              const sc = matchPct(data.questions, data.answers, voterAnswers, tip.id);
              return (
                <g pointerEvents="none">
                  <rect x={bx} y={by} width={182} height={70} rx={8} fill={B.ink} opacity=".95" />
                  <rect x={bx} y={by} width={4} height={70} rx={2} fill={partyCol(tip.party)} />
                  <text
                    x={bx + 14}
                    y={by + 23}
                    fontSize="13"
                    fill="#fff"
                    fontWeight="700"
                    fontFamily="'Poppins', sans-serif"
                  >
                    {tip.name}
                  </text>
                  <text
                    x={bx + 14}
                    y={by + 39}
                    fontSize="10"
                    fill={partyCol(tip.party)}
                    fontWeight="600"
                    fontFamily="'Poppins', sans-serif"
                  >
                    {tip.party}
                    {tip.district != null
                      ? ` · District ${tip.district}${tip.district_2 != null ? ` + ${tip.district_2}` : ''}`
                      : ''}
                  </text>
                  <text
                    x={bx + 14}
                    y={by + 57}
                    fontSize="10"
                    fill={B.gold}
                    fontWeight="700"
                    fontFamily="'Poppins', sans-serif"
                  >
                    {sc}% match with you
                  </text>
                </g>
              );
            })()}
        </svg>
      </div>

      {noResponse.length > 0 && (
        <p
          style={{
            fontFamily: "'Poppins', sans-serif",
            fontSize: 11,
            color: B.muted,
            textAlign: 'center',
            margin: '14px 0 0',
          }}
        >
          Did not respond ({noResponse.length}):{' '}
          {noResponse.map((m) => m.name).join(', ')}
        </p>
      )}

      <div
        className="fu fu3"
        style={{
          display: 'flex',
          gap: 18,
          justifyContent: 'center',
          flexWrap: 'wrap',
          margin: '18px 0 28px',
        }}
      >
        {(['PL', 'PN', 'AD', 'IND', 'MOM', 'AM', 'IMP'] as const).map((p) => (
          <div key={p} style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
            <div
              style={{ width: 13, height: 13, borderRadius: '50%', background: partyCol(p) }}
            />
            <span
              style={{
                fontFamily: "'Poppins', sans-serif",
                fontWeight: 700,
                fontSize: 12,
                color: partyCol(p),
              }}
            >
              {p}
            </span>
          </div>
        ))}
        <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
          <div
            style={{
              width: 13,
              height: 13,
              borderRadius: '50%',
              background: B.gold,
              border: `2px solid ${B.ink}`,
            }}
          />
          <span
            style={{
              fontFamily: "'Poppins', sans-serif",
              fontWeight: 700,
              fontSize: 12,
              color: B.ink,
            }}
          >
            You
          </span>
        </div>
      </div>

      <div style={{ textAlign: 'center' }}>
        <button className="cta" onClick={onNext}>
          See the Full Response Matrix →
        </button>
      </div>
    </div>
  );
}

// ---- Screen 4: Matrix --------------------------------------
function downloadMatrixCsv(data: QuizData, voterAnswers: AnswerMap): void {
  const escape = (v: unknown): string => {
    const s = v == null ? '' : String(v);
    if (/[,"\r\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
    return s;
  };
  const headers = [
    'Name',
    'Party',
    'District',
    'District 2',
    'Role',
    ...data.questions.map((q) => q.text),
  ];
  const lines = [headers.map(escape).join(',')];
  // User row first — anonymous, no party/district, just their answers.
  lines.push(
    [
      'You',
      '',
      '',
      '',
      '',
      ...data.questions.map((q) => voterAnswers[q.id] ?? ''),
    ]
      .map(escape)
      .join(','),
  );
  // One row per MP, in the order data.mps gives us. Empty cells where
  // the MP didn't answer that question.
  for (const mp of data.mps) {
    lines.push(
      [
        mp.name,
        mp.party,
        mp.district ?? '',
        mp.district_2 ?? '',
        mp.role ?? '',
        ...data.questions.map((q) => data.answers[mp.id]?.[q.id] ?? ''),
      ]
        .map(escape)
        .join(','),
    );
  }
  // BOM + CRLF for Excel friendliness on Windows.
  const csv = '﻿' + lines.join('\r\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const today = new Date().toISOString().slice(0, 10);
  const a = document.createElement('a');
  a.href = url;
  a.download = `lovin-malta-quiz-matrix-${today}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  // Defer revoke so the download has a chance to start in some browsers.
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function Matrix({
  data,
  voterAnswers,
  voterDistrict,
}: {
  data: QuizData;
  voterAnswers: AnswerMap;
  voterDistrict: number | null;
}) {
  const [filter, setFilter] = useState<'ALL' | 'PL' | 'PN' | 'AD' | 'IND' | 'MOM' | 'AM' | 'IMP'>('ALL');
  const [districtFilter, setDistrictFilter] = useState<'ALL' | number>(
    voterDistrict ?? 'ALL',
  );
  const visible = data.mps.filter((m) => {
    if (filter !== 'ALL' && m.party !== filter) return false;
    if (!mpInDistrict(m, districtFilter)) return false;
    return true;
  });

  // Districts that actually have at least one MP (so the filter doesn't
  // offer a dropdown of empty options).
  const availableDistricts = availableDistrictsFor(data.mps);

  return (
    <div style={{ maxWidth: 1000, margin: '0 auto', padding: '36px 20px 64px' }}>
      <div className="fu" style={{ marginBottom: 22 }}>
        <h2
          style={{
            fontFamily: "'Poppins', sans-serif",
            fontWeight: 900,
            fontSize: 32,
            color: B.ink,
            lineHeight: 1.1,
            marginBottom: 10,
            textTransform: 'uppercase',
          }}
        >
          Every Answer.
          <br />
          <span style={{ color: B.red }}>Side by Side.</span>
        </h2>
        <p style={{ fontSize: 14, color: B.muted, lineHeight: 1.8, maxWidth: 540 }}>
          Your answers appear in the gold row. Filter by party or district
          below. Empty cells mean the MP did not respond.
        </p>
        <button
          type="button"
          onClick={() => downloadMatrixCsv(data, voterAnswers)}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 8,
            marginTop: 14,
            padding: '10px 18px',
            background: '#fff',
            color: B.ink,
            border: `2px solid ${B.borderAlt}`,
            borderRadius: 6,
            fontFamily: "'Poppins', sans-serif",
            fontWeight: 800,
            fontSize: 12,
            letterSpacing: 1.2,
            textTransform: 'uppercase',
            cursor: 'pointer',
            transition: 'border-color .15s, color .15s',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.borderColor = B.ink;
            e.currentTarget.style.color = B.ink;
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.borderColor = B.borderAlt;
            e.currentTarget.style.color = B.ink;
          }}
          title="Download every MP's answer to every question as a CSV (one row per MP, one column per question, plus your own answers as the first data row)."
        >
          <span aria-hidden="true">⬇</span>
          Download full matrix (CSV)
        </button>
      </div>

      <div
        className="fu fu1"
        style={{
          display: 'flex',
          gap: 8,
          flexWrap: 'wrap',
          marginBottom: 12,
          alignItems: 'center',
        }}
      >
        <span
          style={{
            fontFamily: "'Poppins', sans-serif",
            fontWeight: 700,
            fontSize: 10.5,
            letterSpacing: 1.4,
            textTransform: 'uppercase',
            color: B.muted,
            marginRight: 4,
          }}
        >
          Party
        </span>
        {(['ALL', 'PL', 'PN', 'AD', 'IND', 'MOM', 'AM', 'IMP'] as const).map((p) => (
          <button
            key={p}
            type="button"
            onClick={() => setFilter(p)}
            style={{
              padding: '5px 15px',
              borderRadius: 6,
              border: '2px solid',
              borderColor: p === 'ALL' ? B.ink : partyCol(p),
              background: filter === p ? (p === 'ALL' ? B.ink : partyCol(p)) : 'transparent',
              color: filter === p ? '#fff' : p === 'ALL' ? B.ink : partyCol(p),
              fontFamily: "'Poppins', sans-serif",
              fontWeight: 700,
              fontSize: 12,
              cursor: 'pointer',
            }}
          >
            {p}
          </button>
        ))}
      </div>

      {availableDistricts.length > 0 && (
        <div
          className="fu fu1"
          style={{
            display: 'flex',
            gap: 6,
            flexWrap: 'wrap',
            marginBottom: 16,
            alignItems: 'center',
          }}
        >
          <span
            style={{
              fontFamily: "'Poppins', sans-serif",
              fontWeight: 700,
              fontSize: 10.5,
              letterSpacing: 1.4,
              textTransform: 'uppercase',
              color: B.muted,
              marginRight: 4,
            }}
          >
            District
          </span>
          {(['ALL', ...MALTA_DISTRICTS] as ('ALL' | number)[]).map((d) => {
            const isYours = voterDistrict != null && d === voterDistrict;
            return (
              <button
                key={String(d)}
                type="button"
                onClick={() => setDistrictFilter(d)}
                style={{
                  padding: '5px 12px',
                  borderRadius: 6,
                  border: '2px solid',
                  borderColor:
                    districtFilter === d
                      ? B.ink
                      : isYours
                        ? B.gold
                        : B.borderAlt,
                  background:
                    districtFilter === d ? B.ink : isYours ? B.gold : 'transparent',
                  color:
                    districtFilter === d ? '#fff' : isYours ? B.ink : B.muted,
                  fontFamily: "'Poppins', sans-serif",
                  fontWeight: 700,
                  fontSize: 12,
                  cursor: 'pointer',
                }}
              >
                {d === 'ALL' ? 'All' : `D${d}`}
                {isYours && (
                  <span style={{ marginLeft: 4, fontSize: 9, opacity: 0.8 }}>
                    (yours)
                  </span>
                )}
              </button>
            );
          })}
        </div>
      )}

      <div
        className="fu fu2"
        style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginBottom: 16 }}
      >
        {[1, 2, 3, 4, 5].map((v) => (
          <div key={v} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <div
              style={{ width: 14, height: 14, borderRadius: 3, background: scoreCol(v) }}
            />
            <span style={{ fontSize: 11, color: B.muted, fontWeight: 500 }}>
              {scoreLabel(v)}
            </span>
          </div>
        ))}
      </div>

      <div
        style={{
          overflowX: 'auto',
          borderRadius: 10,
          border: `1px solid ${B.border}`,
        }}
      >
        <table
          style={{
            borderCollapse: 'separate',
            borderSpacing: 0,
            background: '#fff',
            width: '100%',
          }}
        >
          <thead>
            <tr style={{ background: B.ink }}>
              <th
                style={{
                  width: 152,
                  textAlign: 'left',
                  padding: '11px 14px',
                  fontSize: 9,
                  color: '#777',
                  fontFamily: "'Poppins', sans-serif",
                  fontWeight: 700,
                  letterSpacing: 1,
                  textTransform: 'uppercase',
                  borderBottom: `3px solid ${B.red}`,
                  position: 'sticky',
                  left: 0,
                  background: B.ink,
                  zIndex: 10,
                }}
              >
                MP
              </th>
              {data.questions.map((q, i) => (
                <th
                  key={q.id}
                  style={{
                    width: 43,
                    padding: '10px 3px',
                    textAlign: 'center',
                    borderBottom: `3px solid ${q.axis === 'GS' ? B.growthCol : B.stateCol}`,
                    fontSize: 9,
                    color: q.axis === 'GS' ? B.growthCol : B.stateCol,
                    fontFamily: "'Poppins', sans-serif",
                    fontWeight: 700,
                  }}
                >
                  Q{i + 1}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {Object.keys(voterAnswers).length > 0 && (
              <tr style={{ background: '#fffbf0' }}>
                <td
                  style={{
                    padding: '8px 14px',
                    position: 'sticky',
                    left: 0,
                    background: '#fffbf0',
                    zIndex: 5,
                    borderRight: `1px solid ${B.border}`,
                    borderBottom: `2px solid ${B.gold}`,
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
                    <div
                      style={{
                        width: 30,
                        height: 30,
                        borderRadius: '50%',
                        background: B.gold,
                        border: `2px solid ${B.ink}`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontFamily: "'Poppins', sans-serif",
                        fontWeight: 900,
                        fontSize: 8,
                        color: B.ink,
                      }}
                    >
                      YOU
                    </div>
                    <div
                      style={{
                        fontFamily: "'Poppins', sans-serif",
                        fontSize: 11,
                        fontWeight: 700,
                        color: B.ink,
                      }}
                    >
                      Your Answers
                    </div>
                  </div>
                </td>
                {data.questions.map((q) => {
                  const v = voterAnswers[q.id];
                  return (
                    <td
                      key={q.id}
                      style={{
                        textAlign: 'center',
                        padding: '5px 2px',
                        borderBottom: `2px solid ${B.gold}`,
                      }}
                    >
                      <div
                        style={{
                          width: 33,
                          height: 26,
                          borderRadius: 4,
                          margin: '0 auto',
                          background: v ? scoreCol(v) : '#eee',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: '#fff',
                          fontFamily: "'Poppins', sans-serif",
                          fontWeight: 900,
                          fontSize: 12,
                          opacity: v ? 1 : 0.25,
                        }}
                      >
                        {v || '—'}
                      </div>
                    </td>
                  );
                })}
              </tr>
            )}
            {visible.map((mp, ri) => (
              <tr key={mp.id} style={{ background: ri % 2 === 0 ? '#fff' : '#fafafa' }}>
                <td
                  style={{
                    padding: '7px 14px',
                    borderRight: `1px solid ${B.border}`,
                    borderLeft: `3px solid ${partyCol(mp.party)}`,
                    borderBottom: `1px solid ${B.border}`,
                    position: 'sticky',
                    left: 0,
                    background: ri % 2 === 0 ? '#fff' : '#fafafa',
                    zIndex: 5,
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
                    <div
                      style={{
                        width: 29,
                        height: 29,
                        borderRadius: '50%',
                        flexShrink: 0,
                        background: partyCol(mp.party),
                        color: '#fff',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontFamily: "'Poppins', sans-serif",
                        fontWeight: 900,
                        fontSize: 9,
                      }}
                    >
                      {initials(mp.name)}
                    </div>
                    <div>
                      <div
                        style={{
                          fontFamily: "'Poppins', sans-serif",
                          fontSize: 11,
                          fontWeight: 700,
                          color: B.ink,
                          lineHeight: 1.2,
                        }}
                      >
                        {mp.name}
                      </div>
                      <div
                        style={{
                          fontFamily: "'Poppins', sans-serif",
                          fontSize: 9,
                          color: partyCol(mp.party),
                          fontWeight: 700,
                        }}
                      >
                        {mp.party}
                      </div>
                    </div>
                  </div>
                </td>
                {data.questions.map((q) => {
                  const v = data.answers[mp.id]?.[q.id];
                  return (
                    <td
                      key={q.id}
                      style={{
                        textAlign: 'center',
                        padding: '4px 2px',
                        borderBottom: `1px solid ${B.border}`,
                      }}
                    >
                      {v == null ? (
                        <div
                          style={{
                            width: 33,
                            height: 26,
                            borderRadius: 4,
                            margin: '0 auto',
                            background: '#f1f1f1',
                            color: '#aaa',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontFamily: "'Poppins', sans-serif",
                            fontWeight: 700,
                            fontSize: 11,
                          }}
                        >
                          —
                        </div>
                      ) : (
                        <div
                          title={scoreLabel(v)}
                          style={{
                            width: 33,
                            height: 26,
                            borderRadius: 4,
                            margin: '0 auto',
                            background: scoreCol(v),
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: '#fff',
                            fontFamily: "'Poppins', sans-serif",
                            fontWeight: 900,
                            fontSize: 12,
                          }}
                        >
                          {v}
                        </div>
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div style={{ marginTop: 32 }}>
        <div
          style={{
            fontFamily: "'Poppins', sans-serif",
            fontWeight: 700,
            fontSize: 10,
            letterSpacing: 2,
            textTransform: 'uppercase',
            color: B.muted,
            marginBottom: 14,
          }}
        >
          Question Key
        </div>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
            gap: '8px 28px',
          }}
        >
          {data.questions.map((q, i) => (
            <div
              key={q.id}
              style={{ display: 'flex', gap: 10, fontSize: 12, lineHeight: 1.6 }}
            >
              <span
                style={{
                  fontFamily: "'Poppins', sans-serif",
                  fontWeight: 800,
                  color: B.red,
                  minWidth: 26,
                  fontSize: 13,
                  flexShrink: 0,
                }}
              >
                Q{i + 1}
              </span>
              <span style={{ color: B.ink }}>{q.text}</span>
            </div>
          ))}
        </div>
      </div>

      <SummaryCTA data={data} voterAnswers={voterAnswers} />
    </div>
  );
}

// Closes out the in-quiz flow with a prominent CTA that opens the
// shareable summary page (/quiz/result?...). The summary page is now
// the only surface that carries a share button — keeps the in-quiz
// results screens focused on browsing the data, not on social
// distribution.
function SummaryCTA({
  data,
  voterAnswers,
}: {
  data: QuizData;
  voterAnswers: AnswerMap;
}) {
  const [enabled, setEnabled] = useState(false);
  useEffect(() => {
    setEnabled(isShareEnabled());
  }, []);

  const params = useMemo(
    () => computeShareParams(data, voterAnswers),
    [data, voterAnswers],
  );

  if (!enabled || !params) return null;
  const href = `/quiz/result?${params}`;
  return (
    <div style={{ marginTop: 40, paddingTop: 32, borderTop: `1px solid ${B.border}`, textAlign: 'center' }}>
      <a
        href={href}
        className="cta"
        style={{ textDecoration: 'none', display: 'inline-block' }}
      >
        Finish
      </a>
    </div>
  );
}

// ---- App shell ---------------------------------------------
export default function Quiz() {
  // CSS lives at module level; injected once on first mount.
  useEffect(() => {
    const el = document.createElement('style');
    el.textContent = CSS;
    document.head.appendChild(el);
    return () => {
      document.head.removeChild(el);
    };
  }, []);

  const { data, isLoading, error } = useQuiz();

  if (isLoading) {
    return (
      <div className="lm">
        <div className="lm-intro">
          <div className="lm-intro__inner">
            <p style={{ color: B.muted, fontSize: 14 }}>Loading the quiz…</p>
          </div>
        </div>
      </div>
    );
  }
  if (error || !data) {
    return (
      <div className="lm">
        <div className="lm-intro">
          <div className="lm-intro__inner">
            <p style={{ color: B.muted, fontSize: 14 }}>
              The quiz is unavailable right now. Try refreshing the page.
            </p>
          </div>
        </div>
      </div>
    );
  }
  // Friendly empty-state when the DB has no questions — happens on a
  // fresh install or after a wipe before re-upload. Visibility of the
  // module as a whole is controlled by the `quiz` feature flag in
  // /admin/flags, not here.
  if (data.questions.length === 0) {
    return (
      <div className="lm">
        <div className="lm-intro">
          <div className="lm-intro__inner">
            <h2 className="lm-intro__title">No quiz questions loaded yet</h2>
            <p className="lm-intro__lede">
              Upload a Typeform export via /admin/quiz to populate.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return <QuizRunner data={data} />;
}

function QuizRunner({ data }: { data: QuizData }) {
  // Single persisted state object — survives navigation between
  // results steps AND a full page refresh.
  const [s, setS] = useState<PersistedQuizState>(() => loadQuizState());

  useEffect(() => {
    saveQuizState(s);
  }, [s]);

  const patch = (p: Partial<PersistedQuizState>) =>
    setS((prev) => ({ ...prev, ...p }));

  const handleStart = () => {
    // Fire quiz-started only on first start in this browser-storage
    // lifetime. Resetting via "Start over" clears the flag, so a
    // genuine fresh attempt is counted again.
    if (!s.startedTracked) {
      try {
        trackQuizStarted();
      } catch {
        /* swallow — analytics never break the page */
      }
    }
    patch({ started: true, startedTracked: true });
  };

  const handleAnswersChange = (a: AnswerMap) => patch({ voterAnswers: a });

  const go = (next: number) => {
    const newMax = Math.max(s.maxVisitedStep, next);
    // Fire quiz-completed the first time the visitor reaches a
    // results step (step ≥ 1). Mirror the once-per-lifetime semantics.
    if (next >= 1 && !s.completedTracked) {
      try {
        const payload = computeQuizCompletionPayload(
          data,
          s.voterAnswers,
          s.voterDistrict,
        );
        trackQuizCompleted(payload);
      } catch {
        /* swallow — analytics never break the page */
      }
      patch({ step: next, maxVisitedStep: newMax, completedTracked: true });
    } else {
      patch({ step: next, maxVisitedStep: newMax });
    }
    const target = document.getElementById('quiz');
    if (target) target.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const handleRestart = () => {
    if (
      typeof window !== 'undefined' &&
      !window.confirm('Start over? Your current answers will be cleared.')
    ) {
      return;
    }
    clearQuizState();
    setS(QUIZ_DEFAULTS);
    const target = document.getElementById('quiz');
    if (target) target.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  if (!s.started) {
    return (
      <div className="lm">
        <QuizIntro data={data} onStart={handleStart} />
      </div>
    );
  }

  if (!s.districtPicked) {
    return (
      <div className="lm">
        <DistrictPicker
          onPick={(d) => patch({ voterDistrict: d, districtPicked: true })}
        />
      </div>
    );
  }

  return (
    <div className="lm">
      <StepIndicator
        step={s.step}
        maxVisitedStep={s.maxVisitedStep}
        onNav={go}
        onRestart={handleRestart}
      />
      {s.step === 0 && (
        <Questionnaire
          data={data}
          answers={s.voterAnswers}
          onAnswersChange={handleAnswersChange}
          onComplete={() => go(1)}
        />
      )}
      {s.step === 1 && (
        <TopMatches
          data={data}
          voterAnswers={s.voterAnswers}
          voterDistrict={s.voterDistrict}
          onNext={() => go(2)}
        />
      )}
      {s.step === 2 && (
        <Compass
          data={data}
          voterAnswers={s.voterAnswers}
          voterDistrict={s.voterDistrict}
          onNext={() => go(3)}
        />
      )}
      {s.step === 3 && (
        <Matrix
          data={data}
          voterAnswers={s.voterAnswers}
          voterDistrict={s.voterDistrict}
        />
      )}
    </div>
  );
}

// Shared helper used by:
//   · SummaryCTA — builds the href for the "View & share my summary"
//     link at the bottom of the Matrix step
//   · QuizResultPage — re-encodes the params it parsed off the URL
//     (so og:image meta / share-button URLs stay canonical)
//
// Returns the URL search-param string (no leading "?") or null when
// the voter hasn't answered enough questions to rank candidates.
export function computeShareParams(
  data: QuizData,
  voterAnswers: AnswerMap,
): string | null {
  if (Object.keys(voterAnswers).length === 0) return null;
  const proj = projectAnswersShared(data.questions, voterAnswers);
  const ranked = data.mps
    .map((mp) => {
      const shared = data.questions.filter(
        (q) => voterAnswers[q.id] != null && data.answers[mp.id]?.[q.id] != null,
      ).length;
      return {
        mp,
        score: matchPct(data.questions, data.answers, voterAnswers, mp.id),
        shared,
      };
    })
    .filter((r) => r.shared > 0)
    .sort((a, b) => b.score - a.score);
  if (ranked.length === 0) return null;

  const top = ranked[0];
  const top2 = ranked[1] ?? null;
  const top3 = ranked[2] ?? null;
  // Bottom is the LAST entry — but only if it's distinct from the
  // top 3 (so we don't show the same MP as both top and least when
  // the rankings list is very short).
  const bottom = ranked.length > 3 ? ranked[ranked.length - 1] : null;

  const topId = parseInt(top.mp.id, 10);
  if (!Number.isFinite(topId)) return null;
  const parseMpId = (mp?: typeof top.mp): number | null => {
    if (!mp) return null;
    const n = parseInt(mp.id, 10);
    return Number.isFinite(n) ? n : null;
  };

  return encodeShareState({
    x: proj.x,
    y: proj.y,
    topId,
    topName: top.mp.name,
    topParty: top.mp.party,
    topScore: top.score,
    top2Id: parseMpId(top2?.mp),
    top2Name: top2 ? top2.mp.name : null,
    top2Party: top2 ? top2.mp.party : null,
    top2Score: top2 ? top2.score : null,
    top3Id: parseMpId(top3?.mp),
    top3Name: top3 ? top3.mp.name : null,
    top3Party: top3 ? top3.mp.party : null,
    top3Score: top3 ? top3.score : null,
    bottomId: parseMpId(bottom?.mp),
    bottomName: bottom ? bottom.mp.name : null,
    bottomParty: bottom ? bottom.mp.party : null,
    bottomScore: bottom ? bottom.score : null,
  });
}

